import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnChanges, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { EstateControlService } from 'src/app/services/estate-control.service';
import { TabControlService } from 'src/app/services/tab-control.service';
import { capitalizeFirstLetter, formatCurrency, formatDate } from '../../Helper';
import { PDFService } from 'src/app/services/PDF/pdfservice.service';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from 'src/app/services/notification.service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-grant-letter',
  templateUrl: './grant-letter.component.html',
  styleUrls: ['./grant-letter.component.scss']
})
export class GrantLetterComponent implements AfterViewInit, OnChanges {
  @ViewChild('pdfContent', { static: false }) pdfContent!: ElementRef;
  @ViewChild('sigContent') sigContent!: TemplateRef<any>;
  
  service: TabControlService | EstateControlService = this.tabControlService;

  capitalizeFirstLetter = capitalizeFirstLetter;
  contentSections: any[] = []; // Initialize as empty array
  MAX_PAGE_HEIGHT = 1122; // A4 height in pixels at 96dpi

  currentDate: string = '';

  settlorsPrimed: any[] = [];
  settlorList: string = '';

  beneficiaryList: string[] = [];
  guardianPrincipals: any[] = [];
  guardiansAlternate: any[] = [];

  trustOptions: string = 'educational, medical and limited upkeep';
  fundSources: string = '[INSERT SOURCE OF FUNDS]';
  
  private _paymentAmount: string = '';
  private readonly paymentAmountReady = new BehaviorSubject<boolean>(false);

  set paymentAmount(value: string) {
    if (!environment.production) console.log('Setting paymentAmount:', value);
    this._paymentAmount = value;
    this.paymentAmountReady.next(true); // Notify that paymentAmount is ready
  }

  get paymentAmount(): string {
    return this._paymentAmount;
  }

  async waitForPaymentAmount(): Promise<void> {
    await firstValueFrom(this.paymentAmountReady.pipe(filter(ready => ready)));
  }

  formatCurrency = formatCurrency;

  constructor(
    private readonly dialog: MatDialog,
    private readonly tabControlService: TabControlService,
    private readonly notificationService: NotificationService,
    private readonly pdfService: PDFService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.currentDate = formatDate(new Date(), true);
  }

  async ngAfterViewInit() {
    // Organize grant data and set PDF content
    await this.organizeGrantData().then(() => {
      this.cdr.detectChanges();
    });
    // Wait for paymentAmount to be ready
    await this.waitForPaymentAmount();
    
    this.pdfService.setGrantPdfContent(this.pdfContent);

    // Trigger change detection to ensure the view updates
    this.cdr.detectChanges();
  }

  // Add ngOnChanges to handle input changes
  ngOnChanges(changes: SimpleChanges) {
    if (changes['paymentAmount']) {
      this.cdr.detectChanges();
    }
  }

  get region() {
    return this.tabControlService.regionType;
  }

  get settlors() {
    return this.tabControlService.settlors;
  }

  get beneficiaries() {
    return this.tabControlService.beneficiaries;
  }

  get guardians() {
    return this.tabControlService.guardians;
  }

  get trustInfo() {
    return this.tabControlService.trustInfo;
  }

  get paymentData() {
    return this.tabControlService.paymentData;
  }

  get signatureImage() {
    return this.tabControlService.signatureImage;
  }

  get GuardianOptions() {
    return this.tabControlService.GuardianOptions;
  }

  arrayContainsValue(arr: string | null | undefined, value: string) {
    return arr?.includes(value);
  }

  arraysContainValue(arr: any[] | null | undefined, value: string) {
    let exists = false

    if (arr) {
      arr.forEach(element => {
        if (element.TrustPurpose && element.TrustPurpose.length > 0) {
          if (this.arrayContainsValue(element.TrustPurpose, value)) exists = true;
        }
      });
    }
    return exists;
  }

  returnOtherPurposeValue(arr: any[] | null | undefined): string {
    let purposes = ''
    if (arr) {
      arr.forEach((element: any, index: number) => {
        if (element.TrustPurpose && element.TrustPurpose.length > 0) {
          if (this.arrayContainsValue(element.TrustPurpose, 'Other')) {
            if (purposes.length > 0) {
              if (index == arr.length-1) purposes += ` and ${element.OtherPurpose?.toLowerCase()}`;
              else purposes += `, ${element.OtherPurpose?.toLowerCase()}`;
            }
            else purposes = `${element.OtherPurpose?.toLowerCase()}`;
          }
        }
      });
    }
    return purposes;
  }

  organizeGrantData = async() =>  {
    try {
      const data = await this.tabControlService.getData(this.tabControlService.storageKey);
      if (data) {
        await this.tabControlService.updateDataFromStorage();
        if (!environment.production) console.log('harooo !!! ', data)
      }

      if (this.settlors.length > 0) {
        if (this.tabControlService.settlorType == 0) { // Institution
          this.settlorsPrimed = this.settlors[0].Authorisers;
        } else {
          this.settlorsPrimed = this.settlors;
        }

        this.settlorsPrimed.forEach((settlor: any, index: number) => {
          const names =
            `${settlor.FirstName}${settlor.MiddleName && settlor.MiddleName != '' ? ' '+settlor.MiddleName+' ' : ' '}${settlor.LastName}`
          if (this.settlorList.length == 0) this.settlorList = names;
          else {
            if (index == this.settlorsPrimed.length-1) this.settlorList += ` and ${names}`;
            else this.settlorList += `, ${names}`;
          }
        })
      }
    
      if (this.beneficiaries.length > 0) {
        this.trustOptions = '';
        
        this.beneficiaryList = this.beneficiaries
          .filter((b: any) => b.done)
          .map((b: any) => {
            return b.Category === 'Institution' 
              ? `${b.InstitutionName} (${b.PercShare}%)` 
              : `${b.FirstName}${b.MiddleName && b.MiddleName != '' ? ' '+b.MiddleName : ''} ${b.LastName} (${b.PercShare}%)`;
        });

        const containsEducation = this.arraysContainValue(this.beneficiaries, 'Education');
        const containsMedicare = this.arraysContainValue(this.beneficiaries, 'Medicare');
        const containsUpKeep = this.arraysContainValue(this.beneficiaries, 'Upkeep');
          
        if (containsEducation) {
          this.trustOptions += 'educational';
        }
        if (containsMedicare) {
          if (this.trustOptions.length > 3) {
            if (containsUpKeep) this.trustOptions += ', medical';
            else this.trustOptions += ' and medical';
          }
          else this.trustOptions += 'medical';
        }
        if (containsUpKeep) {
          if (this.trustOptions.length > 3) this.trustOptions += ' and limited upkeep';
          else this.trustOptions += 'limited upkeep';
        }
        // Include other purpose
        if (this.arraysContainValue(this.beneficiaries, 'Other')) {
          const otherText = this.returnOtherPurposeValue(this.beneficiaries);

          if (!containsEducation && !containsMedicare && !containsUpKeep) {
            this.trustOptions += ` other purpose(s) listed as: ${otherText}; `;
          } else {
            this.trustOptions += `; and including other purposes listed as: ${otherText}; `;
          }
        }
      }

      if (this.guardians.length > 0) {
        this.guardianPrincipals = this.guardians.filter((g: any, index: number) => index > 0 && g.done && g.Category == this.GuardianOptions[0]);
        this.guardiansAlternate = this.guardians.filter((g: any) => g.done && g.Category == this.GuardianOptions[1]);
      }

      if (this.trustInfo?.SourceOfFunds) {
        if (typeof this.trustInfo.SourceOfFunds == 'string') {this.fundSources = this.trustInfo.SourceOfFunds}
        else {
          if (this.trustInfo.SourceOfFunds.length > 0) {
            this.fundSources = this.trustInfo.SourceOfFunds.join(', ')
          }
        }
      }
      
    } catch (error: any) {
      console.error(`[organizeGrantData] : ${error.message}`) 
    }
  }

  // Contract Formation Handling
  openSignatureModal = (): void => {
    const dialogRef = this.dialog.open(ModalComponent, {
      maxWidth: '1400px',  // Maximum width of the modal
      height: 'fit-content',
      maxHeight: '1100px',
      data: {
        contentTemplate: this.sigContent,
        modalTitle: 'Draw Your Signature',
        buttonText: 'Cancel'
      },
      disableClose: true // Disable closing when clicking outside the modal
    });

    this.notificationService.addModal(dialogRef);

    dialogRef.afterClosed().subscribe(() => {
      this.notificationService.removeModal(dialogRef);
    });
  }

  onSignatureSaved = async(signatureImage: string): Promise<void> => {
    this.tabControlService.signatureImage = signatureImage;

    this.notificationService.closeAllModals();
    this.tabControlService.page6Done = true;
    this.tabControlService.changeTab(7);
    
    this.notificationService.viewToast('success', 'The complete & signed document will be sent to your email once you complete payment')
  }
}
